import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyoutIncludes,
  formatEuro,
  formatInterval,
  PRICE_PERIOD_LABELS,
  type BuyoutInfoDto,
  type ContractCallDto,
  type ContractDetailDto,
  type ContractLineDto,
  type ContractPriceDto,
  type ContractState,
  type ContractSummaryDto,
  type UncoveredObligationDto,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";

export const contractsKey = ["contracts"] as const;
export const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

/** Contracten (alle, of van één gebouw). */
export function useContracts(buildingId?: string) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...contractsKey, "list", buildingId ?? "alle"],
    queryFn: () => api<ContractSummaryDto[]>(`/contracts${buildingId ? `?gebouw=${buildingId}` : ""}`),
    enabled: hasModule(me, "contracts"),
  });
}

export function useContract(id: string) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...contractsKey, "detail", id],
    queryFn: () => api<ContractDetailDto>(`/contracts/${id}`),
    enabled: hasModule(me, "contracts") && !!id,
  });
}

/** Uitvoeringen zonder lopend contract; `contract` = alleen de gebouwen van dat contract (om toe te voegen). */
export function useUncovered(opts: { contract?: string; all?: boolean; enabled?: boolean } = {}) {
  const { data: me } = useMe();
  const q = new URLSearchParams();
  if (opts.contract) q.set("contract", opts.contract);
  if (opts.all) q.set("alle", "1");
  return useQuery({
    queryKey: [...contractsKey, "uncovered", q.toString()],
    queryFn: () => api<UncoveredObligationDto[]>(`/contracts/uncovered${q.size ? `?${q}` : ""}`),
    enabled: hasModule(me, "contracts") && opts.enabled !== false,
  });
}

/** Na een wijziging: contracten, zonder contract en de uitvoeringen bij assets opnieuw ophalen. */
export function useContractsRefresh() {
  const qc = useQueryClient();
  return async (detail?: ContractDetailDto) => {
    if (detail) qc.setQueryData([...contractsKey, "detail", detail.id], detail);
    await Promise.all([
      qc.invalidateQueries({ queryKey: contractsKey }),
      qc.invalidateQueries({ queryKey: ["obligations"] }),
      qc.invalidateQueries({ queryKey: ["obligation-list"] }),
    ]);
  };
}

export const fmtDate = (iso: string | null | undefined) =>
  iso
    ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

export const euro = (c: number | null | undefined) => (c == null ? "" : formatEuro(c));

/** Toon van de stand voor `.chip.tone`. */
export function stateTone(c: Pick<ContractSummaryDto, "state" | "endsSoon">): string {
  if (c.state === "beeindigd") return "tone-danger";
  if (c.endsSoon || c.state === "opgezegd") return "tone-warn";
  if (c.state === "lopend") return "tone-ok";
  return "";
}

export const STATE_ORDER: Record<ContractState, number> = {
  lopend: 0,
  nog_niet: 1,
  opgezegd: 2,
  concept: 3,
  beeindigd: 4,
};

/** "Elke 3 mnd", "Elke week", "Eenmalig". */
export function intervalText(o: { intervalMonths: number | null; intervalWeeks: number | null }) {
  if (o.intervalWeeks) return o.intervalWeeks === 1 ? "Elke week" : `Elke ${o.intervalWeeks} weken`;
  if (!o.intervalMonths) return "Eenmalig";
  return `Elke ${formatInterval(o.intervalMonths)}`;
}

/** Bedrag van een prijs zoals het in de post hoort: "€ 4.800,00 per jaar", "€ 85,00 per keer", tarieven. */
export function priceText(l: Pick<ContractLineDto, "billing" | "priceForm" | "period">, p: ContractPriceDto | null) {
  if (!p) return "Nog geen prijs";
  if (l.billing === "cost_plus") {
    const parts = [
      p.hourlyRateCents != null && `${euro(p.hourlyRateCents)} per uur`,
      p.calloutCents != null && `voorrijden ${euro(p.calloutCents)}`,
      p.materialMarkupPercent != null && `materiaal +${String(p.materialMarkupPercent).replace(".", ",")}%`,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "Tarieven nog niet ingevuld";
  }
  if (p.amountCents == null) return "Nog geen bedrag";
  return l.priceForm === "per_visit"
    ? `${euro(p.amountCents)} per stuk per keer`
    : `${euro(p.amountCents)} ${PRICE_PERIOD_LABELS[l.period ?? "year"]}`;
}

export const pct = (n: number | null) => (n == null ? "" : `${n > 0 ? "+" : ""}${String(n).replace(".", ",")}%`);

/** Storingen (taken) onder de storingsposten van een contract in een jaar. */
export function useContractCalls(id: string, year: number, enabled = true) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...contractsKey, "calls", id, year],
    queryFn: () => api<ContractCallDto[]>(`/contracts/${id}/calls?jaar=${year}`),
    enabled: hasModule(me, "contracts") && !!id && enabled,
  });
}

/** Storingsposten van lopende contracten voor een gebouw (taak koppelen). */
export function useBreakdownLines(buildingId: string | undefined, enabled = true) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...contractsKey, "breakdown-lines", buildingId],
    queryFn: () =>
      api<(BuyoutInfoDto & { assetIds: string[]; buildingId: string | null })[]>(
        `/contracts/breakdown-lines?gebouw=${buildingId}`,
      ),
    enabled: hasModule(me, "contracts") && !!buildingId && enabled,
  });
}

/** Korte samenvatting van een storingspost: wat inbegrepen is, maximum, bereikbaarheid en responstijd. */
export function buyoutParts(l: {
  billing: ContractLineDto["billing"];
  includes?: string;
  includesLabour?: boolean;
  includesCallout?: boolean;
  materialMode?: ContractLineDto["materialMode"];
  materialLimitCents?: number | null;
  maxCallsPerYear: number | null;
  serviceHours: ContractLineDto["serviceHours"];
  responseHours: number | null;
}): string[] {
  const inc =
    l.includes ??
    (l.materialMode
      ? buyoutIncludes(
          {
            includesLabour: l.includesLabour ?? true,
            includesCallout: l.includesCallout ?? true,
            materialMode: l.materialMode,
            materialLimitCents: l.materialLimitCents ?? null,
          },
          formatEuro,
        )
      : "");
  return [
    l.billing === "fixed" ? `Inbegrepen: ${inc}` : "Op nacalculatie",
    l.billing === "fixed" && (l.maxCallsPerYear ? `max. ${l.maxCallsPerYear} per jaar` : "onbeperkt"),
    l.serviceHours === "always" ? "24/7" : "kantooruren",
    l.responseHours ? `binnen ${l.responseHours} uur` : "",
  ].filter((x): x is string => Boolean(x));
}
